/**
 * UserDetailPage Component
 * 
 * Displays detailed information about a specific user, including:
 * - User profile information
 * - Subscription details
 * - List of containers with website counts
 */

import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { typography, surfaces, borders, textColors, interactive, brandColors } from "../design-system/tokens";
import { useAdminChangeUserSubscription, useAdminGrantUserAccess, useUserDetail } from "./hooks/useAdminStats";

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // This page is opened from several admin lists (users, affiliates, leads,
  // feedbacks, deletions), so "back" means the page the admin came from. Callers
  // may name it in `state.from`; otherwise pop the router history entry, and only
  // fall back to the dashboard when the URL was opened directly (nothing to pop).
  const handleBack = () => {
    const fromPath = location.state?.from;
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    // react-router stamps the history index on each pushed entry; 0 means this is
    // the first entry of the session.
    if (Number(window.history?.state?.idx || 0) > 0) {
      navigate(-1);
      return;
    }
    navigate("/admin/dashboard/dashboard");
  };
  const { data: user, isLoading, error } = useUserDetail(userId, true);
  const changeSubMutation = useAdminChangeUserSubscription(userId);
  const grantAccessMutation = useAdminGrantUserAccess(userId);

  const [planDraft, setPlanDraft] = useState("plus");
  const [periodDraft, setPeriodDraft] = useState("monthly");
  const [prorationDraft, setProrationDraft] = useState("create_prorations");
  const [accessPlanDraft, setAccessPlanDraft] = useState("plus");
  const [accessDurationDays, setAccessDurationDays] = useState("30");
  const [notice, setNotice] = useState(null); // { type: 'success'|'error'|'info', message, url? }

  useEffect(() => {
    if (!user?.subscription) return;
    const currentPlan = (user.subscription.plan_name || "free").toLowerCase();
    const currentPeriod = (user.subscription.billing_period || "monthly").toLowerCase();
    setPlanDraft(currentPlan === "free" ? "plus" : currentPlan);
    setPeriodDraft(currentPeriod === "yearly" ? "yearly" : "monthly");
    setAccessPlanDraft(currentPlan === "pro" ? "pro" : "plus");
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const titleize = (value) => {
    const s = String(value || "").trim();
    if (!s) return "N/A";
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const effectiveAccessGrant = user?.subscription?.effective_access_grant || user?.subscription?.access_grant || null;
  const accessGrants = Array.isArray(user?.subscription?.access_grants) ? user.subscription.access_grants : [];
  const allSubscriptionRows = Array.isArray(user?.subscription?.subscriptions) ? user.subscription.subscriptions : [];
  const hasAccessGrant = Boolean(effectiveAccessGrant?.is_active);

  const grantSourceLabel = (source) => {
    const s = String(source || "").trim();
    if (s === "global_access_grant") return "Global grant";
    if (s === "admin_manual") return "Admin grant";
    if (s === "access_coupon") return "Access coupon";
    if (s === "legacy") return "Legacy";
    if (s === "stripe") return "Stripe";
    if (s === "system") return "System";
    return s ? s : "Unknown";
  };

  const subStatusBadge = (status, willRenew) => {
    if (status === "active" && willRenew === false) {
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
    }
    if (status === "active") return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
    if (status === "trialing") return "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200";
    if (status === "scheduled") return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200";
    if (status === "suspended") return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
    return `${surfaces.tertiary} ${textColors.primary}`;
  };

  const subStatusLabel = (status, willRenew) => {
    if (status === "active" && willRenew === false) return "Cancelling";
    return titleize(status);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className={`h-8 ${surfaces.glass} rounded w-1/3`}></div>
            <div className={`h-64 ${surfaces.glass} rounded`}></div>
            <div className={`h-96 ${surfaces.glass} rounded`}></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={handleBack}
            className={`${brandColors.primary} ${brandColors.primaryHover} ${typography.bodySmall} mb-6 flex items-center gap-2`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-12 text-center`}>
            <svg
              className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className={`${typography.h3} ${textColors.primary} mb-2`}>User Not Found</h2>
            <p className={`${typography.body} ${textColors.secondary}`}>
              {error.message || "Failed to load user details"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Defensive: should not happen, but avoids crashing if data is missing.
  if (!user) {
    return (
      <div className="px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-12 text-center`}>
            <h2 className={`${typography.h3} ${textColors.primary} mb-2`}>User Not Found</h2>
            <p className={`${typography.body} ${textColors.secondary}`}>Failed to load user details.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-8 pb-12">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className={`${brandColors.primary} ${brandColors.primaryHover} ${typography.bodySmall} mb-6 flex items-center gap-2`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* User Profile Card */}
        <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6 mb-6`}>
          <div className="flex items-start gap-4 lg:flex-row-reverse">
            {/* User Info */}
            <div className="flex-1 min-w-0">
              <h1 className={`${typography.h2} ${textColors.primary} mb-2 break-words`}>{user.username}</h1>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="min-w-0">
                  <p className={`${typography.label} ${textColors.tertiary} mb-1`}>Email</p>
                  <p className={`${typography.body} ${textColors.primary} break-all`}>{user.email}</p>
                </div>

                {(user.first_name || user.last_name) && (
                  <div className="min-w-0">
                    <p className={`${typography.label} ${textColors.tertiary} mb-1`}>Full Name</p>
                    <p className={`${typography.body} ${textColors.primary}`}>
                      {user.first_name} {user.last_name}
                    </p>
                  </div>
                )}

                <div className="min-w-0">
                  <p className={`${typography.label} ${textColors.tertiary} mb-1`}>Member Since</p>
                  <p className={`${typography.body} ${textColors.primary}`}>
                    {formatDate(user.created_at)}
                  </p>
                </div>

                <div className="min-w-0">
                  <p className={`${typography.label} ${textColors.tertiary} mb-1`}>User ID</p>
                  <p className={`${typography.bodySmall} ${textColors.muted} font-mono break-all`}>{user.id}</p>
                </div>
              </div>
            </div>

            {/* Avatar Placeholder */}
            <div
              className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br ${brandColors.gradientFrom} ${brandColors.gradientTo} flex items-center justify-center flex-shrink-0`}
            >
              <span className="text-2xl font-bold text-white">
                {user.username?.charAt(0).toUpperCase() || "?"}
              </span>
            </div>
          </div>
        </div>

          {/* Subscription Card */}
          {user.subscription && (
            <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6 mb-6`}>
              <h2 className={`${typography.h4} ${textColors.primary} mb-4`}>
                Subscription Details
              </h2>

              {hasAccessGrant && (
                <div className="mb-4 rounded-2xl border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
                  <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
                    Effective access grant active
                  </div>
                  <div className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
                    This user has temporary access to{" "}
                    <span className={`${textColors.primary} font-medium`}>{titleize(effectiveAccessGrant?.plan_name)}</span>{" "}
                    without payment. Access ends on{" "}
                    <span className={`${textColors.primary} font-medium`}>{formatDate(effectiveAccessGrant?.expires_at)}</span>.
                  </div>
                  <div className={`${typography.caption} ${textColors.tertiary} mt-1`}>
                    Source: {grantSourceLabel(effectiveAccessGrant?.grant_source)}
                    {effectiveAccessGrant?.code ? (
                      <>
                        {" · Code: "}
                        <span className="break-all">{effectiveAccessGrant.code}</span>
                      </>
                    ) : null}
                  </div>
                </div>
              )}

              {accessGrants.length > 0 && (
                <div className={`mb-4 rounded-2xl border ${borders.default} ${surfaces.secondary} px-4 py-3`}>
                  <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>All active access grants</div>
                  <div className="mt-2 space-y-2">
                    {accessGrants.map((g) => (
                      <div
                        key={g.id}
                        className={`flex flex-col md:flex-row md:items-center md:justify-between gap-1 rounded-2xl ${surfaces.glass} ${borders.glass} border px-3 py-2`}
                      >
                        <div className={`${typography.bodySmall} ${textColors.primary}`}>
                          <span className="font-medium">{titleize(g.plan_name)}</span>
                          <span className={`ml-2 ${typography.caption} ${textColors.tertiary}`}>
                            ({grantSourceLabel(g.grant_source)})
                          </span>
                          {g.code ? (
                            <span className={`ml-2 ${typography.caption} ${textColors.tertiary}`}>
                              Code: <span className="break-all">{g.code}</span>
                            </span>
                          ) : null}
                        </div>
                        <div className={`${typography.caption} ${textColors.secondary}`}>
                          Expires: <span className={`${textColors.primary} font-medium`}>{formatDate(g.expires_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {allSubscriptionRows.length > 0 && (
                <div className={`mb-4 rounded-2xl border ${borders.default} ${surfaces.secondary} px-4 py-3`}>
                  <div className={`${typography.bodySmall} ${textColors.primary} font-medium mb-2`}>All subscription rows</div>

                  {/* Mobile: cards */}
                  <div className="sm:hidden space-y-2">
                    {allSubscriptionRows.map((row) => (
                      <div key={row.id} className={`rounded-2xl ${surfaces.glass} ${borders.glass} border px-3 py-2 space-y-1`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className={`${typography.bodySmall} ${textColors.primary} font-medium`}>{titleize(row.plan)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${subStatusBadge(row.status, row.will_renew)}`}>
                            {subStatusLabel(row.status, row.will_renew)}
                          </span>
                        </div>
                        <div className={`${typography.caption} ${textColors.secondary}`}>
                          Source: {grantSourceLabel(row.source)}
                          {row.billing_period ? ` · ${titleize(row.billing_period)}` : ""}
                          {row.price > 0 ? ` · $${Number(row.price).toFixed(2)}` : ""}
                        </div>
                        <div className={`${typography.caption} ${textColors.tertiary}`}>
                          {row.current_period_start ? `Starts: ${formatDate(row.current_period_start)}` : ""}
                          {row.current_period_start && row.current_period_end ? " · " : ""}
                          {row.current_period_end ? `Ends: ${formatDate(row.current_period_end)}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop: table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className={`border-b ${borders.default}`}>
                          <th className={`${typography.caption} ${textColors.tertiary} pb-2 pr-3`}>Plan</th>
                          <th className={`${typography.caption} ${textColors.tertiary} pb-2 pr-3`}>Source</th>
                          <th className={`${typography.caption} ${textColors.tertiary} pb-2 pr-3`}>Status</th>
                          <th className={`${typography.caption} ${textColors.tertiary} pb-2 pr-3`}>Billing</th>
                          <th className={`${typography.caption} ${textColors.tertiary} pb-2 pr-3`}>Starts</th>
                          <th className={`${typography.caption} ${textColors.tertiary} pb-2`}>Ends</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allSubscriptionRows.map((row) => (
                          <tr key={row.id} className={`border-b ${borders.default} last:border-b-0`}>
                            <td className={`${typography.bodySmall} ${textColors.primary} py-2 pr-3 font-medium`}>{titleize(row.plan)}</td>
                            <td className={`${typography.caption} ${textColors.secondary} py-2 pr-3`}>{grantSourceLabel(row.source)}</td>
                            <td className="py-2 pr-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${subStatusBadge(row.status, row.will_renew)}`}>
                                {subStatusLabel(row.status, row.will_renew)}
                              </span>
                            </td>
                            <td className={`${typography.caption} ${textColors.secondary} py-2 pr-3`}>
                              {row.billing_period ? titleize(row.billing_period) : "—"}
                              {row.price > 0 ? ` · $${Number(row.price).toFixed(2)}` : ""}
                            </td>
                            <td className={`${typography.caption} ${textColors.secondary} py-2 pr-3`}>{row.current_period_start ? formatDate(row.current_period_start) : "—"}</td>
                            <td className={`${typography.caption} ${textColors.secondary} py-2`}>{row.current_period_end ? formatDate(row.current_period_end) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {notice && (
                <div
                  className={`mb-4 rounded-2xl border px-4 py-3 ${
                    notice.type === "error"
                      ? "border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20"
                      : notice.type === "success"
                      ? "border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-900/20"
                      : "border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20"
                  }`}
                >
                  <p
                    className={`${typography.bodySmall} ${
                      notice.type === "error"
                        ? "text-red-700 dark:text-red-200"
                        : notice.type === "success"
                        ? "text-green-700 dark:text-green-200"
                        : "text-blue-700 dark:text-blue-200"
                    }`}
                  >
                    {notice.message}
                  </p>
                  {notice.url && (
                    <div className="mt-2 flex items-center gap-3">
                      <a
                        href={notice.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${interactive.link} ${interactive.linkHover} ${typography.bodySmall}`}
                      >
                        Open Checkout
                      </a>
                    </div>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className={`${typography.label} ${textColors.tertiary} mb-1`}>
                    Plan
                  </p>
                  <p className={`${typography.h5} ${textColors.primary}`}>
                    {titleize(user.subscription.plan_name)}
                    {hasAccessGrant ? (
                      <span className={`ml-2 align-middle px-2 py-0.5 rounded-full ${typography.caption} ${surfaces.glass} ${textColors.secondary}`}>
                        Access grant
                      </span>
                    ) : null}
                  </p>
                </div>
                
                <div>
                  <p className={`${typography.label} ${textColors.tertiary} mb-1`}>
                    Status
                  </p>
                  <span className={`
                    ${typography.bodySmall}
                    px-2 py-1 rounded
                    ${user.subscription.status === 'active' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                      : `${surfaces.tertiary} ${textColors.primary}`
                    }
                  `}>
                    {user.subscription.status}
                  </span>
                </div>
                
                <div>
                  <p className={`${typography.label} ${textColors.tertiary} mb-1`}>
                    Billing Period
                  </p>
                  <p className={`${typography.h5} ${textColors.primary}`}>
                    {titleize(user.subscription.billing_period)}
                  </p>
                </div>
                
                <div>
                  <p className={`${typography.label} ${textColors.tertiary} mb-1`}>
                    Period Ends
                  </p>
                  <p className={`${typography.body} ${textColors.primary}`}>
                    {formatDate(user.subscription.current_period_end)}
                  </p>
                </div>
                
                <div>
                  <p className={`${typography.label} ${textColors.tertiary} mb-1`}>
                    Will Renew
                  </p>
                  <p className={`${typography.body} ${textColors.primary}`}>
                    {user.subscription.will_renew ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>

              <div className={`mt-6 pt-5 border-t ${borders.default}`}>
                <h3 className={`${typography.h5} ${textColors.primary} mb-3`}>Admin: Change Plan</h3>
                <p className={`${typography.bodySmall} ${textColors.secondary} mb-4`}>
                  Stripe is the billing source of truth. If the user has no Stripe subscription yet (free plan), this will generate a Checkout link instead of forcing an upgrade.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <p className={`${typography.label} ${textColors.tertiary} mb-1`}>Target plan</p>
                    <select
                      className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glassCard} px-3 py-2 ${textColors.primary}`}
                      value={planDraft}
                      onChange={(e) => setPlanDraft(e.target.value)}
                    >
                      <option value="free">Free (schedule cancel)</option>
                      <option value="plus">Plus</option>
                      <option value="pro">Pro</option>
                    </select>
                  </div>

                  <div>
                    <p className={`${typography.label} ${textColors.tertiary} mb-1`}>Billing period</p>
                    <select
                      className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glassCard} px-3 py-2 ${textColors.primary}`}
                      value={periodDraft}
                      onChange={(e) => setPeriodDraft(e.target.value)}
                      disabled={planDraft === "free"}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>

                  <div>
                    <p className={`${typography.label} ${textColors.tertiary} mb-1 flex items-center gap-2`}>
                      Proration
                      <span className={`${typography.caption} ${textColors.muted} whitespace-nowrap`}>
                        (charge/credit for mid-cycle changes)
                      </span>
                    </p>
                    <select
                      className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glassCard} px-3 py-2 ${textColors.primary}`}
                      value={prorationDraft}
                      onChange={(e) => setProrationDraft(e.target.value)}
                      disabled={planDraft === "free"}
                    >
                      <option value="create_prorations">Create prorations</option>
                      <option value="none">No proration</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      setNotice(null);
                      try {
                        const res = await changeSubMutation.mutateAsync({
                          plan_name: planDraft,
                          billing_period: planDraft === "free" ? null : periodDraft,
                          proration_behavior: prorationDraft,
                          return_url: window.location.href,
                        });
                        if (res?.status === "checkout_required" && res?.url) {
                          setNotice({
                            type: "info",
                            message: "User has no Stripe subscription yet. Use this Checkout link to complete the upgrade.",
                            url: res.url,
                          });
                        } else if (res?.status === "scheduled_cancel") {
                          setNotice({ type: "success", message: "Cancellation scheduled at period end." });
                        } else if (res?.status === "updated") {
                          setNotice({
                            type: "success",
                            message: "Subscription updated in Stripe.",
                          });
                        } else {
                          setNotice({ type: "success", message: "Action completed." });
                        }
                      } catch (e) {
                        setNotice({ type: "error", message: e?.message || "Failed to change subscription." });
                      }
                    }}
                    disabled={changeSubMutation.isPending}
                    className={`inline-flex items-center rounded-2xl px-4 py-2 ${typography.button} ${brandColors.primaryBg} ${brandColors.primaryBgHover} text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {changeSubMutation.isPending ? "Working…" : "Apply change"}
                  </button>
                </div>

                <div className={`mt-6 pt-5 border-t ${borders.default}`}>
                  <h3 className={`${typography.h5} ${textColors.primary} mb-3`}>Admin: Grant Access (no payment)</h3>
                  <p className={`${typography.bodySmall} ${textColors.secondary} mb-4`}>
                    Grant temporary access to a paid plan. The user will auto-revert when the access ends.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <p className={`${typography.label} ${textColors.tertiary} mb-1`}>Access plan</p>
                      <select
                        className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glassCard} px-3 py-2 ${textColors.primary}`}
                        value={accessPlanDraft}
                        onChange={(e) => setAccessPlanDraft(e.target.value)}
                      >
                        <option value="plus">Plus</option>
                        <option value="pro">Pro</option>
                      </select>
                    </div>

                    <div>
                      <p className={`${typography.label} ${textColors.tertiary} mb-1`}>Duration (days)</p>
                      <input
                        type="number"
                        min="1"
                        className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glassCard} px-3 py-2 ${textColors.primary}`}
                        value={accessDurationDays}
                        onChange={(e) => setAccessDurationDays(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={async () => {
                        setNotice(null);
                        try {
                          const duration = Number(accessDurationDays || 0);
                          const res = await grantAccessMutation.mutateAsync({
                            plan_name: accessPlanDraft,
                            duration_days: duration,
                          });
                        if (res?.status === "no_change") {
                          const eff = res?.effective_access_grant;
                          setNotice({
                            type: "info",
                            message: eff?.expires_at
                              ? `No change — user already has ${titleize(eff.plan_name)} until ${formatDate(eff.expires_at)}.`
                              : "No change — user already has equal or better access.",
                          });
                        } else if (res?.status === "access_granted") {
                            setNotice({
                              type: "success",
                              message: `Access granted for ${duration} days.`,
                            });
                        } else if (res?.status === "access_extended") {
                          const eff = res?.effective_access_grant;
                          setNotice({
                            type: "success",
                            message: eff?.expires_at
                              ? `Access updated — effective ${titleize(eff.plan_name)} until ${formatDate(eff.expires_at)}.`
                              : "Access updated.",
                          });
                          } else {
                            setNotice({ type: "success", message: "Access granted." });
                          }
                        } catch (e) {
                          setNotice({ type: "error", message: e?.message || "Failed to grant access." });
                        }
                      }}
                      disabled={grantAccessMutation.isPending}
                      className={`inline-flex items-center rounded-2xl px-4 py-2 ${typography.button} ${brandColors.primaryBg} ${brandColors.primaryBgHover} text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {grantAccessMutation.isPending ? "Working…" : "Grant access"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Containers Section */}
          <div className={`${surfaces.glassCard} ${borders.glass} border rounded-2xl p-6`}>
            <h2 className={`${typography.h4} ${textColors.primary} mb-4`}>
              Website Containers ({user.containers?.length || 0})
            </h2>
            
            {!user.containers || user.containers.length === 0 ? (
              <div className="text-center py-12">
                <svg className={`w-16 h-16 ${textColors.muted} mx-auto mb-4`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className={`${typography.body} ${textColors.secondary}`}>
                  No containers yet
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {user.containers.map((container) => (
                  <div
                    key={container.id}
                    className={`${surfaces.glass} ${borders.glass} border rounded-2xl p-4 hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className={`${typography.h5} ${textColors.primary} mb-2 break-words`}>
                          {container.name}
                        </h3>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <svg className={`w-4 h-4 ${textColors.muted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                            <a
                              href={`https://${container.domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${interactive.link} block min-w-0 truncate`}
                              title={container.domain}
                            >
                              {container.domain}
                            </a>
                          </div>
                          
                          <div className={`${typography.caption} ${textColors.muted}`}>
                            Created: {formatDate(container.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <div className={`${surfaces.glassCard} ${borders.glass} border rounded px-3 py-1 text-center`}>
                          <div className={`${typography.h5} ${textColors.primary}`}>
                            {container.website_count}
                          </div>
                          <div className={`${typography.caption} ${textColors.muted}`}>
                            Versions
                          </div>
                        </div>
                        
                        <div className={`${surfaces.glassCard} ${borders.glass} border rounded px-3 py-1 text-center`}>
                          <div className={`${typography.h5} ${textColors.primary}`}>
                            {container.published_count}
                          </div>
                          <div className={`${typography.caption} ${textColors.muted}`}>
                            Published
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default UserDetailPage;

